import { Link } from 'react-router-dom';

import { Api } from '../lib/api.js';
import { roleSummary } from '../lib/agent-text.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Badge, Card, Empty, ErrorState, Hash, Skeleton } from '../components/ui.jsx';

/**
 * Dashboard. One API call, because the first screen after sign-in should not be
 * six round trips.
 *
 * Deliberately not a token screen: §11.1 forbids turning this into a price or
 * portfolio view, so points appear as product state and nothing here
 * implies market value.
 */
export function Dashboard() {
  const { address, sessionReady, isGuest } = useWallet();
  const { data, loading, error, reload } = useApi(() => Api.dashboard(), [sessionReady], { skip: !sessionReady });

  if (error) return <ErrorState error={error} onRetry={reload} context="The dashboard could not be loaded" />;
  if (!sessionReady || loading || !data) return <Skeleton rows={6} />;

  const { agents, recentRuns, counts, engagement, cor, chain } = data;

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">Operations</h1>
          {address ? (
            <p className="page-subtitle mono">{address}</p>
          ) : (
            <p className="page-subtitle">Working off chain. Connect a wallet to mint passports and record actions on chain.</p>
          )}
        </div>
      </header>

      <div className="metrics">
        {/* §5.6: transactions and owners are reported separately and never
            conflated. There is exactly one owner on this screen by definition,
            so a "users" figure would be meaningless here and is absent. */}
        <Metric label="Active agents" value={agents.length} sub={`of 5 maximum`} />
        <Metric label="Terminal runs" value={counts.total_runs} />
        <Metric label="Confirmed proofs" value={counts.confirmed_proofs} />
        <Metric label="Proofs pending" value={counts.pending_proofs} tone={counts.pending_proofs > 0 ? 'pending' : undefined} />
        <Metric label="Chain transactions" value={counts.total_transactions} />
        <Metric label="Active workflows" value={counts.active_workflows} />
      </div>

      <div className="grid-2">
        <Card title="Roster" action={<Link className="link" to="/agents">Manage</Link>}>
          {agents.length === 0 ? (
            <Empty
              title="No agents yet"
              description="Create your first specialist and mint its passport to begin."
              action={<Link className="button button--primary" to="/agents">Create an agent</Link>}
            />
          ) : (
            <ul className="list">
              {agents.map((a) => {
                const e = engagement?.[a.id];
                return (
                  <li key={a.id} className="list__row">
                    <div>
                      <Link className="list__title" to={`/agents/${a.id}`}>{a.name}</Link>
                      <p className="list__meta">{roleSummary(a.role)}</p>
                    </div>
                    <div className="list__aside">
                      {a.status !== 'active' && <Badge tone="pending">{a.status.replace('_', ' ')}</Badge>}
                      {a.passportTokenId && <Badge tone="brand">Passport #{a.passportTokenId}</Badge>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Recent runs" action={<Link className="link" to="/activity">All activity</Link>}>
          {recentRuns.length === 0 ? (
            <Empty title="No runs yet" description="Open an agent and send it a task." />
          ) : (
            <ul className="list">
              {recentRuns.map((r) => (
                <li key={r.id} className="list__row">
                  <div>
                    <Link className="list__title" to={`/runs/${r.id}`}>
                      {agents.find((a) => a.id === r.agent_id)?.name ?? 'Run'}
                    </Link>
                    <p className="list__meta">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <div className="list__aside">
                    {r.terminal_outcome && <Badge tone={r.terminal_outcome === 'completed' ? 'success' : 'neutral'}>{r.terminal_outcome}</Badge>}
                    <Badge tone={r.proof_state === 'Confirmed and indexed' ? 'success' : 'pending'}>
                      {r.proof_state === 'Confirmed and indexed' ? 'Proof complete' : 'Proof pending'}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Chain">
          <dl className="facts">
            <Fact label="Network" value={`opBNB · chain ${chain.chainId}`} />
            {chain.contracts && (
              <>
                <Fact label="Engagement" value={<Hash value={chain.contracts.CortisEngagement} label="contract" />} />
                <Fact label="Passport" value={<Hash value={chain.contracts.AgentPassport} label="contract" />} />
              </>
            )}
            {!chain.contracts && <Fact label="Contracts" value={<span className="text-muted">Not configured</span>} />}
          </dl>
        </Card>

        <Card title="$COR">
          {/* §7.9 and §14.1: a truthful disabled state. No zero balances, no
              placeholder vault, nothing that implies a live token. */}
          {cor.enabled ? (
            <Link className="link" to="/cor">Open the token surface</Link>
          ) : (
            <div className="notice">
              <p className="notice__title">Pre-TGE</p>
              <p className="notice__body">
                $COR is not live. Cortis runs on opBNB gas only. Points are non-transferable product counters and
                are not a promise of a token, an airdrop, or financial value.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, tone }) {
  return (
    <div className={`metric${tone ? ` metric--${tone}` : ''}`}>
      <span className="metric__value">{value}</span>
      <span className="metric__label">{label}</span>
      {sub && <span className="metric__sub">{sub}</span>}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="facts__row">
      <dt className="facts__label">{label}</dt>
      <dd className="facts__value">{value}</dd>
    </div>
  );
}
